/**
 * Xero Sync Status Component
 * Displays and manages Xero sync status and operations
 */

import React from 'react';
import './XeroSyncStatus.css';

const XeroSyncStatus = ({ syncStatus = {}, onSyncAll, isProcessing }) => {

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateSyncPercentage = () => {
    const total = syncStatus?.total_invoices || 0;
    const synced = syncStatus?.synced_invoices || 0;
    return total > 0 ? Math.round((synced / total) * 100) : 0;
  };

  return (
    <div className="xero-sync-status">
      {/* Sync Statistics */}
      <div className="sync-statistics">
        <h3>Sync Statistics</h3>
        <div className="sync-stats-grid">
          <div className="sync-stat">
            <span className="stat-label">Total Invoices</span>
            <span className="stat-value">{syncStatus.total_invoices || 0}</span>
          </div>
          <div className="sync-stat">
            <span className="stat-label">Synced</span>
            <span className="stat-value synced">{syncStatus.synced_invoices || 0}</span>
          </div>
          <div className="sync-stat">
            <span className="stat-label">Pending</span>
            <span className="stat-value pending">{syncStatus.pending_invoices || 0}</span>
          </div>
          <div className="sync-stat">
            <span className="stat-label">Failed</span>
            <span className="stat-value failed">{syncStatus.failed_invoices || 0}</span>
          </div>
          <div className="sync-stat">
            <span className="stat-label">Not Synced</span>
            <span className="stat-value not-synced">{syncStatus.not_synced_invoices || 0}</span>
          </div>
          <div className="sync-stat">
            <span className="stat-label">Last Sync</span>
            <span className="stat-value">
              {formatDate(syncStatus.last_sync_date)}
            </span>
          </div>
        </div>
      </div>

      {/* Sync Progress */}
      <div className="sync-progress">
        <h3>Sync Progress</h3>
        <div className="progress-container">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${calculateSyncPercentage()}%` }}
            ></div>
          </div>
          <div className="progress-text">
            {calculateSyncPercentage()}% of invoices synced
          </div>
        </div>
      </div>

      {/* Sync Actions */}
      <div className="sync-actions">
        <h3>Sync Actions</h3>
        <div className="actions-grid">
          <button
            className="btn btn-primary"
            onClick={onSyncAll}
            disabled={isProcessing}
          >
            <i className="fas fa-sync"></i>
            Sync All Invoices
          </button>
        </div>
      </div>

      {/* Recent Errors */}
      {syncStatus?.errors && syncStatus.errors.length > 0 && (
        <div className="sync-errors">
          <h3>Recent Errors</h3>
          <div className="error-list">
            {syncStatus.errors.slice(0, 5).map((error, index) => (
              <div key={index} className="error-item">
                <div className="error-header">
                  <span className="error-type">{error.type}</span>
                  <span className="error-time">{formatDate(error.timestamp)}</span>
                </div>
                <div className="error-message">{error.message}</div>
                {error.details && (
                  <div className="error-details">{error.details}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sync Information */}
      <div className="sync-info">
        <h3>Sync Information</h3>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Auto Sync:</span>
            <span className="info-value">
              {syncStatus.auto_sync_enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Sync Frequency:</span>
            <span className="info-value">
              {syncStatus.sync_frequency || 'Manual'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Next Sync:</span>
            <span className="info-value">
              {formatDate(syncStatus.next_sync_date)}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Last Error:</span>
            <span className="info-value">
              {formatDate(syncStatus.last_error_date)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default XeroSyncStatus;
